/**
 * Table Component
 *
 * A flexible table component with sorting, selection, and pagination support.
 */

import { type ReactNode, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// ============================================================================
// Table Root
// ============================================================================

export interface TableProps extends HTMLAttributes<HTMLTableElement> {}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div className="w-full overflow-auto">
        <table
          ref={ref}
          className={cn('w-full caption-bottom text-sm', className)}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';

// ============================================================================
// Table Header
// ============================================================================

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <thead
        ref={ref}
        className={cn('bg-slate-50 border-b border-slate-200', className)}
        {...props}
      >
        {children}
      </thead>
    );
  }
);

TableHeader.displayName = 'TableHeader';

// ============================================================================
// Table Body
// ============================================================================

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tbody
        ref={ref}
        className={cn('divide-y divide-slate-200', className)}
        {...props}
      >
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

// ============================================================================
// Table Row
// ============================================================================

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean;
  isClickable?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, isSelected, isClickable, children, onClick, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
      if (onClick) {
        Logger.debug(LogCategory.UI, 'Table row clicked', 'TableRow');
        onClick(e);
      }
    };

    return (
      <tr
        ref={ref}
        className={cn(
          'transition-colors',
          isClickable && 'cursor-pointer hover:bg-slate-50',
          isSelected && 'bg-primary-50',
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

// ============================================================================
// Table Head Cell
// ============================================================================

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable, sorted, onSort, children, ...props }, ref) => {
    const handleSort = () => {
      if (sortable && onSort) {
        Logger.debug(LogCategory.UI, 'Table column sort clicked', 'TableHead', {
          currentSort: sorted,
        });
        onSort();
      }
    };

    return (
      <th
        ref={ref}
        className={cn(
          'h-12 px-4 text-left align-middle font-semibold text-slate-600',
          sortable && 'cursor-pointer select-none hover:text-slate-900',
          className
        )}
        onClick={handleSort}
        {...props}
      >
        <div className="flex items-center gap-1">
          {children}
          {sortable && (
            <span className="text-slate-400">
              {sorted === 'asc' ? (
                <ChevronUp className="h-4 w-4" />
              ) : sorted === 'desc' ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronsUpDown className="h-4 w-4" />
              )}
            </span>
          )}
        </div>
      </th>
    );
  }
);

TableHead.displayName = 'TableHead';

// ============================================================================
// Table Cell
// ============================================================================

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <td
        ref={ref}
        className={cn('px-4 py-3 align-middle text-slate-700', className)}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

// ============================================================================
// Table Caption
// ============================================================================

export interface TableCaptionProps extends HTMLAttributes<HTMLTableCaptionElement> {}

export const TableCaption = forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <caption
        ref={ref}
        className={cn('mt-4 text-sm text-slate-500', className)}
        {...props}
      >
        {children}
      </caption>
    );
  }
);

TableCaption.displayName = 'TableCaption';

// ============================================================================
// Table Empty State
// ============================================================================

export interface TableEmptyProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  colSpan: number;
}

export function TableEmpty({ icon, title, description, action, colSpan }: TableEmptyProps) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-64">
        <div className="flex flex-col items-center justify-center text-center">
          {icon && <div className="text-slate-300 mb-4">{icon}</div>}
          <h3 className="text-lg font-medium text-slate-900">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-slate-500 max-w-sm">{description}</p>
          )}
          {action && <div className="mt-4">{action}</div>}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// Table Loading State
// ============================================================================

export interface TableLoadingProps {
  colSpan: number;
  rows?: number;
}

export function TableLoading({ colSpan, rows = 5 }: TableLoadingProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({ length: colSpan }).map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <div className="h-4 bg-slate-200 rounded animate-pulse" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
